import React, { useEffect, useState } from 'react';
import { Student, Location } from '../../../types';
import { formatTimeRemaining, getStatus } from '../../../utils/timeUtils';
import { X, Clock, MapPin } from 'lucide-react';

interface StudentProfileCardProps {
  student: Student;
  locations: Location[];
  onClose: () => void;
}

// Helper to generate a 6-digit student ID starting with 33
function getDisplayStudentId(student: Student) {
  // Use a hash of the UUID to get a 4-digit number, always positive
  let hash = 0;
  for (let i = 0; i < student.userId.length; i++) {
    hash = ((hash << 5) - hash) + student.userId.charCodeAt(i);
    hash |= 0;
  }
  const num = Math.abs(hash % 10000);
  return `33${num.toString().padStart(4, '0')}`;
}

// Helper to assign a grade (7th or 8th) based on hash of student name
function getStudentGrade(student: Student) {
  const hash = student.name.charCodeAt(0) + student.name.charCodeAt(student.name.length - 1);
  return hash % 2 === 0 ? '7th Grade' : '8th Grade';
}

// Helper to get a realistic randomuser.me avatar URL
function getRealisticAvatarUrl(student: Student) {
  // Hash the name to get a number 1-99
  let hash = 0;
  for (let i = 0; i < student.name.length; i++) {
    hash = ((hash << 5) - hash) + student.name.charCodeAt(i);
    hash |= 0;
  }
  const num = Math.abs(hash % 99) + 1; // 1-99
  // Alternate gender based on hash
  const gender = hash % 2 === 0 ? 'men' : 'women';
  return `https://randomuser.me/api/portraits/${gender}/${num}.jpg`;
}

export const StudentProfileCard: React.FC<StudentProfileCardProps> = ({
  student,
  locations,
  onClose,
}) => {
  const startLocation = locations.find(loc => loc.id === student.startLocationId);
  const destination = locations.find(loc => loc.id === student.destinationId);
  const status = getStatus(student.expectedReturnTime);

  // Live countdown timer
  const [timeRemaining, setTimeRemaining] = useState(formatTimeRemaining(student.expectedReturnTime - Date.now()));
  useEffect(() => {
    const interval = setInterval(() => {
      setTimeRemaining(formatTimeRemaining(student.expectedReturnTime - Date.now()));
    }, 1000);
    return () => clearInterval(interval);
  }, [student.expectedReturnTime]);

  if (!startLocation || !destination) return null;

  const displayId = getDisplayStudentId(student);
  const grade = getStudentGrade(student);
  const avatarUrl = getRealisticAvatarUrl(student);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-xl font-semibold text-stone-800">Student Profile</h2>
          <button
            onClick={onClose}
            className="text-stone-500 hover:text-stone-700 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Student Info */}
          <div className="flex items-center space-x-3">
            <div className="bg-amber-100 p-2 rounded-full">
              <img
                src={avatarUrl}
                alt={student.name}
                className="h-14 w-14 rounded-full border border-amber-200 bg-white"
                style={{ objectFit: 'cover' }}
              />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-stone-800">{student.name}</h3>
              <p className="text-sm text-stone-500">Student ID: {displayId}</p>
              <p className="text-sm text-stone-500">{grade}</p>
            </div>
          </div>

          {/* Compact Info Section */}
          <div className="bg-stone-50 rounded-lg p-3 flex flex-col gap-2">
            <div className="flex items-center gap-3 text-sm">
              <Clock className="h-4 w-4 text-amber-600 mr-1" />
              <span className={`font-medium ${
                status === 'on-time'
                  ? 'text-green-600'
                  : status === 'warning'
                  ? 'text-yellow-600'
                  : 'text-red-600'
              }`}>{timeRemaining}</span>
              <span className="mx-2 text-stone-400">|</span>
              <MapPin className="h-4 w-4 text-amber-600 mr-1" />
              <span>
                {startLocation.name} <span className="mx-1 text-stone-400">→</span> <span className="font-semibold">{destination.name}</span>
              </span>
            </div>
            <div className="flex items-center gap-3 text-xs text-stone-500 mt-1">
              <span>Start: <span className="font-medium text-stone-700">{new Date(student.startTime).toLocaleTimeString()}</span></span>
              <span className="mx-2 text-stone-300">|</span>
              <span>Return: <span className="font-medium text-stone-700">{new Date(student.expectedReturnTime).toLocaleTimeString()}</span></span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 